import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useNotification } from '@/hooks/utils/notification';
import { useLoggedUser } from '../user/useLoggedUser';

// Same-origin: Vite (dev) and nginx (Docker) proxy /socket.io to the API.

type NotificationMetadata = {
  orderId?: string;
  status?: string;
  roomId?: string;
  messageId?: string;
  variantId?: string;
  productId?: string;
  available?: number;
  threshold?: number;
};

interface NotificationPayload {
  title: string;
  message: string;
  category: 'ORDER' | 'CHAT' | 'STOCK' | 'REFUND' | 'DELIVERY';
  metadata?: NotificationMetadata;
}

// Socket instance outside of the hook to maintain a single instance
let globalSocket: Socket | null = null;

export const useWebSocket = () => {
  const { userId } = useLoggedUser();
  const { showNotification } = useNotification();
  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const connectionTimeoutRef = useRef<NodeJS.Timeout>();

  // Memoize the notification handler to prevent unnecessary re-renders
  const handleNotification = useCallback(
    (notification: NotificationPayload) => {
      console.log('[WebSocket] Received notification:', notification);
      showNotification({
        type: 'info',
        message: notification.title,
        description: notification.message,
      });

      // Trigger a custom event to update notification count
      window.dispatchEvent(new CustomEvent('new-notification'));
    },
    [showNotification],
  );

  // Initialize socket connection
  const initializeSocket = useCallback(() => {
    if (!userId) {
      console.log('[WebSocket] No userId available, skipping connection');
      return;
    }

    // If we already have a socket and it's connected, don't create a new one
    if (globalSocket?.connected) {
      console.log('[WebSocket] Using existing connection');
      return;
    }

    // If we have a socket but it's not connected, disconnect it first
    if (globalSocket) {
      console.log('[WebSocket] Cleaning up existing connection');
      globalSocket.disconnect();
    }

    console.log('[WebSocket] Initializing new connection for user:', userId);

    // Create new socket instance
    globalSocket = io({
      query: { userId },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      withCredentials: true,
      forceNew: false,
      multiplex: true,
    });

    // Set connection timeout
    connectionTimeoutRef.current = setTimeout(() => {
      if (!isConnected && globalSocket) {
        console.error('[WebSocket] Connection timeout');
        globalSocket.disconnect();
      }
    }, 10000);

    // Connection status handlers
    globalSocket.on('connect', () => {
      console.log('[WebSocket] Connected successfully');
      console.log(
        '[WebSocket] Transport:',
        globalSocket?.io.engine.transport.name,
      );
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;

      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    });

    globalSocket.on('disconnect', reason => {
      console.log('[WebSocket] Disconnected. Reason:', reason);
      setIsConnected(false);
    });

    globalSocket.on('connect_error', error => {
      console.error('[WebSocket] Connection error:', error.message);
      setIsConnected(false);
      reconnectAttemptsRef.current++;

      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        console.error('[WebSocket] Max reconnection attempts reached');
        showNotification({
          type: 'error',
          message: 'Connection Failed',
          description:
            'Unable to establish real-time connection. Please refresh the page.',
        });
      }
    });

    globalSocket.on('reconnect', attemptNumber => {
      console.log('[WebSocket] Reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
    });

    globalSocket.on('reconnect_error', error => {
      console.error('[WebSocket] Reconnection error:', error.message);
      setIsConnected(false);
    });

    globalSocket.on('reconnect_failed', () => {
      console.error('[WebSocket] Failed to reconnect after all attempts');
      setIsConnected(false);
    });

    globalSocket.on('error', error => {
      console.error('[WebSocket] Socket error:', error);
    });

    // Listen for notifications
    globalSocket.on('notification', handleNotification);
  }, [userId, handleNotification]);

  // Effect to handle socket lifecycle
  useEffect(() => {
    initializeSocket();

    // Cleanup function
    return () => {
      console.log('[WebSocket] Cleaning up connection');
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      // Don't disconnect the socket here, as it's now managed globally
    };
  }, [initializeSocket]); // Only depend on initializeSocket

  return {
    socket: globalSocket,
    isConnected,
  };
};
